import { MEMBER_ORDER_SKU_MAX_LENGTH } from '../orders/orders';
import { z } from 'zod';

import { nrtIsoInstantSchema } from './instant';
import { NRT_OTHER_DETAIL_MAX, nrtSideEffectSchema } from './vocabularies';

// ─── NRT request bodies ──────────────────────────────────────────────────────
//
// Every schema here is `.strict()` per ADR-135 Rule 1: these are inputs the
// server rejects unknown keys on. That is load-bearing rather than tidy — a
// non-strict schema silently STRIPS an unexpected key, so a caller that
// believed it sent something meaningful would get a success response for a
// request the server partly ignored. Stripping is how a request succeeds into
// a state nobody asked for.

/** Upper bound on a staff reject reason. */
export const NRT_REJECT_REASON_MAX = 500;
/** Upper bound on a single line of staff free text (note, carrier, tracking). */
export const NRT_SHORT_TEXT_MAX = 200;
/** Upper bound on the quantity of any one item on an order. */
export const NRT_ITEM_QUANTITY_MAX = 99;

/**
 * Member-facing create body.
 *
 * Deliberately absent, and each absence is a decision the spec records:
 *
 * - **No address field.** The server resolves the shipping address (latest
 *   `shipping` row, else latest `home`) and binds its id to the order. A
 *   client-chosen address would move that fallback into the client and hand it
 *   authorship of a PHI row id.
 * - **No item, sku or quantity field.** The member states intent; staff choose
 *   the products. With `.strict()`, a body carrying `items` is a 400 rather
 *   than a silently-stripped success.
 */
export const nrtRequestCreateRequestSchema = z
  .object({
    /** Product intent. At least one must be true — see the refinement below. */
    wantsGum: z.boolean(),
    wantsPatch: z.boolean(),
    /**
     * What the member reported experiencing. Closed on the wire; anything
     * beyond `none` travels in `otherDetail`.
     */
    sideEffects: z.array(nrtSideEffectSchema),
    /** Free-text detail, required with `other` and forbidden without it. */
    otherDetail: z.string().trim().min(1).max(NRT_OTHER_DETAIL_MAX).optional(),
    /**
     * Optional client-generated retry key, scoped to the member.
     *
     * Distinct from the `request_awaiting_approval` refusal, which they are easy
     * to confuse. That refusal answers "this member already has a request
     * awaiting review" — a business rule about a SECOND, deliberate request.
     * Note it is NOT "a request in flight": an `approved` or `shipped` order no
     * longer refuses, because the decision the gate waits on has been made.
     * This key answers "this is the SAME request I already sent" — network
     * safety.
     *
     * Without it the two are indistinguishable on the wire: a mobile retry
     * after a timeout, where the write landed but the response was lost,
     * currently gets the same `409 request_awaiting_approval` as a genuine
     * double-tap, and the client cannot tell whether its order exists. With
     * it, the handler replays the original result instead of refusing.
     *
     * Optional so no caller is forced to adopt it and mobile costs nothing;
     * the handler slice honours it when present.
     */
    idempotencyKey: z.string().trim().min(16).max(128).optional(),
    /**
     * Optional guard against the read/write race: the address the member saw
     * when the form was rendered. When it no longer matches the freshly
     * resolved current address, the write is refused with `address_changed`
     * rather than shipping somewhere they did not confirm.
     */
    expectedAddressId: z.string().uuid().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    // A request selecting no product would create an order that ships nothing
    // while looking, to the member, exactly like one that will.
    if (!value.wantsGum && !value.wantsPatch) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['wantsGum'],
        message: 'at least one of wantsGum or wantsPatch must be true',
      });
    }

    const unique = new Set(value.sideEffects);
    if (unique.size !== value.sideEffects.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sideEffects'],
        message: 'sideEffects must not repeat a value',
      });
    }

    // `none` is an answer, not a modifier: pairing it with anything else is
    // contradictory and would have to be resolved by guesswork downstream.
    if (unique.has('none') && unique.size > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sideEffects'],
        message: "sideEffects cannot combine 'none' with another value",
      });
    }

    // Both directions. The forward one (detail without `other`) is ordinary
    // validation; the reverse one is the important half — `other` with no
    // detail is a member reporting something and the system storing nothing,
    // which is the silent drop this contract exists to remove.
    const selectedOther = unique.has('other');
    if (value.otherDetail !== undefined && !selectedOther) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherDetail'],
        message: "otherDetail is only accepted when sideEffects includes 'other'",
      });
    }
    if (selectedOther && value.otherDetail === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['otherDetail'],
        message: "otherDetail is required when sideEffects includes 'other'",
      });
    }
  });
export type NrtRequestCreateRequest = z.infer<typeof nrtRequestCreateRequestSchema>;

/**
 * Staff-facing item set for an order — the DESIRED FINAL SET, applied as a
 * wholesale replace. A SKU absent from the array is not on the order.
 *
 * `quantity` is an integer of at least 1. Zero is rejected rather than read as
 * removal: the legacy Atlas mutation sends every SKU for the substance on every
 * write, unselected ones carrying `quantity: 0`, because it has no
 * replace-the-set semantic and must express removal positively. A full replace
 * says the same thing without encoding "not ordered" as a stored row, so a zero
 * here is a malformed request rather than a second spelling of absence.
 *
 * An EMPTY array is legal and means the order has no items — explicit, and
 * distinguishable from the zero-row encoding. An order in that state cannot be
 * approved (`order_has_no_items`).
 */
export const nrtOrderItemsWriteRequestSchema = z
  .object({
    items: z.array(
      z
        .object({
          // BOUNDED BY THE DB, not by the generic short-text cap. The CHECK
          // constraint `member_order_items_sku_check` is built from this same
          // constant, so the wire cannot accept a SKU the write would reject
          // with a 23514 after the request was already acknowledged. The
          // generic cap was 200 against a column bounded at 64.
          sku: z.string().trim().min(1).max(MEMBER_ORDER_SKU_MAX_LENGTH),
          quantity: z.number().int().min(1).max(NRT_ITEM_QUANTITY_MAX),
        })
        .strict(),
    ),
  })
  .strict()
  .superRefine((value, ctx) => {
    const skus = value.items.map((item) => item.sku);
    if (new Set(skus).size !== skus.length) {
      // Left to last-write-wins, a duplicate would silently halve or double
      // what ships without the caller ever being told which line survived.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items'],
        message: 'items must not repeat a sku',
      });
    }
  });
export type NrtOrderItemsWriteRequest = z.infer<typeof nrtOrderItemsWriteRequestSchema>;

/** Staff approve. The note is internal and never rendered to the member. */
export const nrtOrderApproveRequestSchema = z
  .object({
    // Bounded by the short-text constant, which documents itself as the bound
    // for notes — not the reject-reason limit. The two are different lengths
    // for different reasons, and an approve note is not a member-facing
    // explanation.
    note: z.string().trim().min(1).max(NRT_SHORT_TEXT_MAX).optional(),
    /**
     * The caller's claim to have seen the member's free-text detail.
     *
     * REQUIRED (as `true`) when the order's request carries `otherDetail`, and
     * FORBIDDEN when it does not — neither of which this schema can know, so
     * both are the handler's to enforce. Optional here on purpose: a schema
     * that required it unconditionally would make every approve carry it,
     * including the requests with nothing to acknowledge, and an attestation
     * the client always sends attests to nothing.
     *
     * Not defaulted. A default of `false` would turn a client that omits the
     * field into one that explicitly denies having looked; a default of `true`
     * would restore the silent approval the field exists to prevent.
     */
    acknowledgedOtherDetail: z.boolean().optional(),
  })
  .strict();
export type NrtOrderApproveRequest = z.infer<typeof nrtOrderApproveRequestSchema>;

/**
 * Staff reject. `reason` is REQUIRED and non-blank.
 *
 * It is what the member's `declined` state is built from — not verbatim (the
 * staff reason may carry clinical detail that is not member-appropriate; the
 * handler maps it), but a rejection with no reason leaves the member with a
 * refusal they cannot act on, which is the invariant's failure mode expressed
 * through the staff surface rather than the member one.
 */
export const nrtOrderRejectRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(NRT_REJECT_REASON_MAX),
  })
  .strict();
export type NrtOrderRejectRequest = z.infer<typeof nrtOrderRejectRequestSchema>;

/** Staff mark-shipped. Every field is optional; manual fulfilment has no tracking. */
export const nrtOrderShipRequestSchema = z
  .object({
    trackingReference: z.string().trim().min(1).max(NRT_SHORT_TEXT_MAX).optional(),
    carrier: z.string().trim().min(1).max(NRT_SHORT_TEXT_MAX).optional(),
    shippedAt: nrtIsoInstantSchema.optional(),
  })
  .strict();
export type NrtOrderShipRequest = z.infer<typeof nrtOrderShipRequestSchema>;

/**
 * Every request schema in this contract, so a test can assert the `.strict()`
 * posture across all of them by iteration rather than by naming each one — a
 * schema added later is covered without anyone remembering to extend the test.
 */
export const NRT_REQUEST_SCHEMAS = {
  nrtRequestCreateRequestSchema,
  nrtOrderItemsWriteRequestSchema,
  nrtOrderApproveRequestSchema,
  nrtOrderRejectRequestSchema,
  nrtOrderShipRequestSchema,
} as const;
